/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBook,
    faChevronDown,
    faGear,
    faWindowClose,
} from '@fortawesome/pro-light-svg-icons';
import { DateTime } from 'luxon';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { subject } from '@casl/ability';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import styles from './ReadingsSection.scss';
import { COLORS, SIZES } from '../../constants/theme';
import Button from '../shared/Button';
import { Can } from '../../auth/Can';
import ModalDialog from '../shared/ModalDialog/Base';
import customerDataRequest from '../../services/api/customer-data';
import { getAuthToken } from '../../auth/authHelpers';
import { cacheInvalidation } from '../../utils/cacheInvalidation';

const READING_MODE = {
    ENG: 'english',
    SPN: 'spanish',
};

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Next Sunday'];
const DAYS_SPANISH = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo siguiente'];

const ReadingsSection = ({
    reading,
    org,
    listingId,
    startOfWeek,
    pageSlug,
}) => {
    const theme = useTheme();
    const { getAccessTokenSilently } = useAuth0();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        description,
        description_spanish: descriptionSpanish,
        image_english_url: imageEnglishUrl,
        image_spanish_url: imageSpanishUrl,
        image_art_url: imageArtUrl,
    } = reading;
    const {
        salesforce_id: salesforceId,
        weekly_readings_enabled: weeklyReadingsEnabled,
    } = org;

    const [readingMode, setReadingMode] = useState(READING_MODE.ENG);
    const [sectionEnable, setSectionEnabled] = useState(weeklyReadingsEnabled);
    const [isEnableModalOpen, setIsEnableModalOpen] = useState(false);
    const [isReadingsModalOpen, setIsReadingsModalOpen] = useState(false);

    useEffect(() => {
        setSectionEnabled(weeklyReadingsEnabled);
    }, [weeklyReadingsEnabled]);

    const toggleReadingMode = () => {
        if (readingMode === READING_MODE.ENG) {
            setReadingMode(READING_MODE.SPN);
        } else {
            setReadingMode(READING_MODE.ENG);
        }
    };

    const handleClick = async (enableFlag) => {
        const token = await getAuthToken(getAccessTokenSilently);

        try {
            await customerDataRequest('put', `/organizations/${salesforceId}/weekly-readings-enabled`, token, {
                weekly_readings_enabled: enableFlag,
            });

            setSectionEnabled(enableFlag);
            setIsEnableModalOpen(false);
            await cacheInvalidation(`/organizations/slug/${pageSlug}`, token);
            toast.success(`Weekly Readings ${enableFlag ? 'Enabled' : 'Disabled'}`);
        } catch {
            setSectionEnabled(!enableFlag);
            setIsEnableModalOpen(false);
            toast.error(`There's an Error ${enableFlag ? 'enabling' : 'disabling'} Weekly Readings`);
        }
    };

    const getReadingText = () => {
        const textSections = [];

        if (!reading?.slug) {
            return textSections;
        }

        const splitReadings = reading[readingMode === READING_MODE.ENG ? 'reading' : 'reading_spanish'].split('\n');

        for (let i = 0; i < splitReadings.length; i++) {
            const r = splitReadings[i];
            const linkDate = startOfWeek.minus({ days: 0 - textSections.length }).toFormat('MMddyy');

            const data = r.split(/([\w\séá]+:( [\w\d]\S?:)?)(.*)/g).filter((j) => j);
            if (!data || data.length === 0) {
                continue;
            }

            const title = data[0].substring(0, data[0].length - 1);
            if ((readingMode === READING_MODE.ENG && !DAYS.includes(title))
                || (readingMode === READING_MODE.SPN && !DAYS_SPANISH.includes(title))) {
                continue;
            }

            const readingForDays = data[1] && data[1].trim().split('/').join(' / ');
            const hreflUrl = readingMode === READING_MODE.ENG
                ? `https://bible.usccb.org/bible/readings/${linkDate}.cfm`
                : `https://bible.usccb.org/es/bible/lecturas/${linkDate}.cfm`;
            // Need to use index as a key as we don't have any other ways to distinguish parts
            // eslint-disable-next-line function-paren-newline
            textSections.push(
                <div key={i}>
                    <div className={styles.daysHeading}>{title}</div>
                    <div className={styles.days}>
                        <a
                            href={hreflUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {readingForDays}
                        </a>
                    </div>
                </div>);

            // All days and Next Sunday added, return. Necessary to avoid adding days from 'Observances' section
            if (textSections.length === 8) {
                return textSections;
            }
        }
        return textSections;
    };

    const readingConfigButtonElement = (
        <>
            <Button
                size={SIZES.small}
                color={COLORS.flat}
                onClick={() => toggleReadingMode()}
            >
                <FontAwesomeIcon icon={faBook} />
                <span className={styles.buttonText}>
                    {readingMode === READING_MODE.ENG ? 'Ver en Español' : 'View in English'}
                </span>
            </Button>
            <Can I="admin" this={subject('claimed', { id: listingId })}>
                <Button
                    leftMargin
                    size={SIZES.small}
                    color={COLORS.flat}
                    onClick={() => setIsEnableModalOpen(true)}
                >
                    <FontAwesomeIcon icon={faGear} />
                    <span className={styles.buttonText}>
                        Disable
                    </span>
                </Button>
            </Can>
        </>
    );

    return (
        <>
            {!sectionEnable ? (
                <Can I="admin" this={subject('claimed', { id: listingId })}>
                    <Grid
                        container
                        spacing={2}
                    >
                        <Grid
                            className={styles.title}
                            size={8}
                        >
                            Weekly Readings
                        </Grid>
                        <Grid
                            className={styles.buttonRight}
                            size={4}
                        >
                            <Button
                                size={SIZES.small}
                                color={COLORS.flat}
                                onClick={() => setIsEnableModalOpen(true)}
                            >
                                <FontAwesomeIcon icon={faGear} />
                                <span className={styles.buttonText}>
                                    Enable
                                </span>
                            </Button>
                        </Grid>
                    </Grid>
                </Can>
            ) : (
                <>
                    {!isSmallScreen && (
                        <Grid
                            className={styles.readingConfigContainer}
                            container
                            spacing={1}
                        >
                            <Grid
                                className={styles.title}
                                size={{
                                    xs: 12,
                                    sm: 8,
                                }}
                            >
                                {readingMode === READING_MODE.ENG ? description : descriptionSpanish}
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 4,
                                }}
                            >
                                {readingConfigButtonElement}
                            </Grid>
                        </Grid>
                    )}
                    <Box className={styles.readingsCompositionContainer}>
                        <Grid
                            container
                            spacing={1}
                        >
                            <Grid
                                size={{
                                    xs: 12,
                                    lg: 6,
                                }}
                            >
                                <Grid
                                    className={styles.mainImageContainer}
                                    size={12}
                                >
                                    {imageArtUrl && (
                                        <img
                                            className={styles.image}
                                            src={imageArtUrl}
                                            alt="Reading Art"
                                            style={{
                                                width: isSmallScreen ? '100%' : '80%',
                                            }}
                                            width="200"
                                            height="200"
                                        />
                                    )}
                                </Grid>
                                {isSmallScreen ? (
                                    <Grid size={12}>
                                        <Accordion expanded={false}>
                                            <AccordionSummary
                                                className={styles.tealText}
                                                expandIcon={(
                                                    <FontAwesomeIcon
                                                        className={styles.tealText}
                                                        icon={faChevronDown}
                                                    />
                                                )}
                                                aria-controls="panel1-content"
                                                id="panel1-header"
                                                onClick={() => setIsReadingsModalOpen(true)}
                                            >
                                                View Readings
                                            </AccordionSummary>
                                        </Accordion>
                                    </Grid>
                                ) : (
                                    <Grid
                                        className={styles.textImageContainer}
                                        size={12}
                                    >
                                        <img
                                            className={styles.image}
                                            src={readingMode === READING_MODE.ENG ? imageEnglishUrl : imageSpanishUrl}
                                            alt={readingMode === READING_MODE.ENG ? description : descriptionSpanish}
                                            style={{
                                                width: isSmallScreen ? '100%' : '80%',
                                            }}
                                            width="200"
                                            height="50"
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            {!isSmallScreen && (
                                <Grid
                                    size={{
                                        xs: 12,
                                        lg: 6,
                                    }}
                                >
                                    {getReadingText()}
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </>
            )}
            <ModalDialog
                title={`${(sectionEnable ? 'Disable' : 'Enable')} Weekly Readings?`}
                content={(
                    <>
                        <p>
                            Weekly readings will
                            {sectionEnable ? ' no longer ' : ' '}
                            be displayed.
                        </p>
                        <p>
                            You may
                            {sectionEnable ? ' enable ' : ' disable '}
                            them again at any time.
                        </p>
                    </>
                )}
                open={isEnableModalOpen}
                onClose={() => setIsEnableModalOpen(false)}
                buttons={(
                    <>
                        <Button
                            onClick={() => setIsEnableModalOpen(false)}
                            color={COLORS.flat}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={sectionEnable
                                ? () => handleClick(false)
                                : () => handleClick(true)}
                            color={COLORS.flat}
                            autoFocus
                        >
                            {sectionEnable ? 'Disable' : 'Enable'}
                        </Button>
                    </>
                )}
                // Close instantly so the content of the dialog changing is
                // not visible to the user when transitioning away.
                transitionDuration={{
                    exit: 0,
                }}
            />
            <Modal
                open={isReadingsModalOpen}
                onClose={() => setIsReadingsModalOpen(false)}
            >
                <Box
                    className={styles.weeklyReadingsModal}
                    sx={{
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 2,
                    }}
                >
                    {readingConfigButtonElement}
                    <IconButton
                        aria-label="close"
                        onClick={() => setIsReadingsModalOpen(false)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: 'grey.500',
                        }}
                    >
                        <FontAwesomeIcon icon={faWindowClose} />
                    </IconButton>

                    <Typography
                        id="modal-modal-description"
                        sx={{
                            mt: 2,
                        }}
                    >
                        {getReadingText()}
                    </Typography>
                </Box>
            </Modal>
        </>
    );
};

ReadingsSection.propTypes = {
    reading: PropTypes.objectOf(PropTypes.any),
    org: PropTypes.objectOf(PropTypes.any),
    listingId: PropTypes.string,
    startOfWeek: PropTypes.objectOf(PropTypes.any),
    pageSlug: PropTypes.string.isRequired,
};

ReadingsSection.defaultProps = {
    reading: {},
    org: {},
    listingId: '',
    startOfWeek: DateTime.now().startOf('week').minus({ days: 1 }),
};

export default ReadingsSection;
